import React from 'react';
import { Trans } from '@lingui/react';

import Link from '../../core/Link/Link';
import Paragraph from '../../core/Paragraph/Paragraph';
import Text, { Props as TextProps } from '../../core/Text/Text';

import {
  TextWithSingleInlineLinkProps,
  validateParagraphText,
} from './TextWithSingleInlineLink.common';

/** Props for web TextWithSingleInlineLink component */
export type Props = TextWithSingleInlineLinkProps &
  Omit<TextProps, 'children' | 'as' | 'webProps' | 'rxnProps'>;

/**
 * TextWithSingleInlineLink is a helper component that ensures good a11y behavior for inline links on React Native.
 * Includes screen reader support with custom accessibility behavior.
 *
 * READ THE DOCS: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/5186093720/TextWithSingleInlineLink
 */
const TextWithSingleInlineLink: React.FC<Props> = ({
  asParagraph = false,
  paragraphText,
  href,
  target,
  size,
  textColor,
  variant,
  underline,
  fontWeight,
  linkFontWeight,
  onClick,
  ...textProps
}) => {
  validateParagraphText(paragraphText);
  const TypogComponent = asParagraph ? Paragraph : Text;

  // Extract webProps for refining
  const { webProps = {} } = textProps;

  // Build component-specific props based on component type
  let typogProps;
  if (TypogComponent === Text) {
    typogProps = textProps;
  } else {
    typogProps = {
      ...textProps,
      // For Paragraph, ensure webProps compatibility
      webProps: {
        ...webProps,
        // Filter out incompatible 'as' values for Paragraph
        ...(webProps.as && !['p', 'div', 'label', 'span'].includes(webProps.as)
          ? { as: 'p' as const }
          : { as: webProps.as }),
      },
    };
  }

  return (
    <TypogComponent size={size} fontWeight={fontWeight} textColor={textColor} {...typogProps}>
      <Trans
        id={paragraphText}
        message={paragraphText}
        components={[
          // @ts-expect-error
          <Link
            href={href}
            target={target}
            asInlineLink
            size={size}
            underline={underline}
            fontWeight={linkFontWeight || fontWeight}
            variant={variant}
            onPress={onClick}
            key={paragraphText}
          />,
        ]}
      />
    </TypogComponent>
  );
};

export default TextWithSingleInlineLink;
